/**
 * Real-user monitoring (WC-30, ruling 0.11): one sample per page load with the page-load time
 * and an error count, posted only when the health read advertises `capabilities.rum`.
 *
 * Privacy by construction: the payload type has three members and no more, a closed browser
 * family replaces the user agent, the error listener counts events and never reads one, and
 * nothing here knows an identifier, a URL or a message. The server refuses any other field.
 */
import { RUM_PATH } from "../contracts/constants.ts";
import type { Capabilities } from "../contracts/envelope.ts";
import type { Scope } from "./dom.ts";
import { FAMILIES, type Family } from "./support.ts";
import type { Mono } from "./time.ts";

/** What leaves the browser: the whole sample. */
export interface RumPayload {
  readonly load_ms: number;
  readonly error_count: number;
  readonly family: Family;
}

export interface Rum {
  /** Whether the server advertised the endpoint. */
  readonly active: boolean;
  /** Count one client-side error (the event itself is never read). */
  recordError(): void;
  /** Adopt the server's advertised capabilities. */
  advertise(capabilities: Capabilities): void;
  /** Post the sample once; `false` when nothing was posted. */
  flush(loadMs: number): Promise<boolean>;
}

export type Post = (path: string, payload: RumPayload) => Promise<void>;

/** The largest body the server accepts (2 KiB). */
export const RUM_BODY_MAX = 2048;
/** The largest load time and error count the server accepts (`ssi.api.rum`). */
export const RUM_LOAD_MS_MAX = 600_000;
export const RUM_ERROR_COUNT_MAX = 1000;

const clamp = (value: number, maximum: number): number =>
  Math.min(maximum, Math.max(0, Math.trunc(value)));

/** Build the sample: bounded integers and a family from the closed set, nothing else. */
export function buildSample(loadMs: number, errorCount: number, family: Family): RumPayload {
  return {
    load_ms: clamp(loadMs, RUM_LOAD_MS_MAX),
    error_count: clamp(errorCount, RUM_ERROR_COUNT_MAX),
    family: FAMILIES.includes(family) ? family : "other",
  };
}

/** Encode a sample as the request body. */
export function encodeSample(sample: RumPayload): string {
  return JSON.stringify(sample);
}

/** Create the monitor; until the capability is advertised it is a no-op that never posts. */
export function createRum(family: Family, post: Post): Rum {
  let errors = 0;
  let active = false;
  let sent = false;
  return {
    get active() {
      return active;
    },
    recordError: () => {
      errors += 1;
    },
    advertise: (capabilities) => {
      active = capabilities.rum;
    },
    flush: async (loadMs) => {
      if (!active || sent) {
        return false;
      }
      sent = true;
      await post(RUM_PATH, buildSample(loadMs, errors, family));
      return true;
    },
  };
}

/** Count `error` and `unhandledrejection` events; the listeners read nothing from the event. */
export function listenForErrors(win: Window, scope: Scope, rum: Rum): void {
  for (const type of ["error", "unhandledrejection"]) {
    scope.listen(win, type, () => {
      rum.recordError();
    });
  }
}

/** The page-load time in milliseconds: the navigation timing once loaded, else the monotonic age. */
export function pageLoadMs(win: Window, mono: Mono): number {
  const [entry] = win.performance.getEntriesByType("navigation");
  const loaded = (entry as PerformanceNavigationTiming | undefined)?.loadEventEnd ?? 0;
  return loaded > 0 ? loaded : mono.now();
}
